import React from 'react'
import { defineMessages } from 'react-intl'
import { Link } from 'react-router-dom'

import ExploreRoutes from 'containers/ExploreRoutes'
import ProjectRoutes from 'containers/ProjectRoutes'
import Languages from 'containers/Languages/Loadable'
import EmbeddedProjectPage from 'containers/EmbeddedProjectPage/Loadable'
import { AuthCallback } from 'hooks/useCurrentUser'

import NotFound from 'containers/NotFound'

import genders from 'data/genders/messages'
import languages from 'data/languages/messages'
import categories from 'data/categories/messages'
import EmbeddedSyntheticVoices from 'containers/EmbeddedSyntheticVoices'

export const messages = defineMessages({
  explore: {
    id: 'route.explore',
    defaultMessage: '/*',
  },
  exploreByLanguage: {
    id: 'route.explore.language',
    defaultMessage: '/{language}-voice-over',
  },
  exploreByLanguageAndGender: {
    id: 'route.explore.language-and-gender',
    defaultMessage: '/{language}-voice-over/{gender}',
  },
  project: {
    id: 'route.project',
    defaultMessage: '/project/*',
  },
  embedded: {
    id: 'route.embedded',
    defaultMessage: '/embedded/:hashId',
  },
  embeddedSyntheticVoices: {
    id: 'route.embeddedSyntheticVoices',
    defaultMessage: '/embedded-synthetic-voices',
  },
  languages: {
    id: 'route.languages',
    defaultMessage: '/native-languages',
  },
  authCallback: {
    id: 'route.authCallback',
    defaultMessage: '/auth/callback',
  },
  notFound: {
    id: 'route.notFound',
    defaultMessage: '/looking-for-a-specific-voice',
  },
  health: {
    id: 'route.health',
    defaultMessage: '/health',
  },
})

export const breadcrumbs = defineMessages({
  explore: {
    id: 'breadcrumb.explore',
    defaultMessage: 'Explore',
  },
  project: {
    id: 'breadcrumb.project',
    defaultMessage: 'Share',
  },
  languages: {
    id: 'breadcrumb.languages',
    defaultMessage: 'Native Languages',
  },
})

const reverseLookupMessage = (container, value, formatMessage) => {
  const foundKey = Object.keys(container).find(
    (key) => formatMessage(container[key]) === value
  )

  return formatMessage(container[foundKey.replace(/URL/, '')])
}

const config = {
  notFound: {
    exact: true,
    component: NotFound,
  },
  health: {
    exact: true,
    component: () => <h1>Health OK</h1>,
  },
  explore: {
    exact: true,
    component: ExploreRoutes,
    breadcrumb:
      (formatMessage) =>
      ({ match: { url } }) =>
        formatMessage(breadcrumbs.explore),
  },
  exploreByLanguageAndGender: {
    exact: true,
    component: ExploreRoutes,
    breadcrumb:
      (formatMessage) =>
      ({
        match: {
          url,
          params: { gender, language },
        },
      }) =>
        reverseLookupMessage(genders, gender, formatMessage),
  },
  exploreByLanguage: {
    exact: true,
    component: ExploreRoutes,
    breadcrumb:
      (formatMessage) =>
      ({
        match: {
          url,
          params: { language },
        },
      }) =>
        reverseLookupMessage(languages, language, formatMessage),
  },
  project: {
    exact: true,
    component: ProjectRoutes,
    breadcrumb:
      (formatMessage) =>
      ({ match: { url } }) =>
        formatMessage(breadcrumbs.project),
  },
  embedded: {
    exact: true,
    component: EmbeddedProjectPage,
  },
  embeddedSyntheticVoices: {
    exact: true,
    component: EmbeddedSyntheticVoices,
  },
  languages: {
    exact: true,
    component: Languages,
  },
  authCallback: {
    exact: true,
    component: AuthCallback,
  },
}

const pathValues = Object.entries(config).reduce((obj, [key, _route]) => {
  obj[key] = [...messages[key].defaultMessage.matchAll(/\{(.*?)\}/g)].reduce(
    (o, [_, k]) => {
      o[k] = `:${k}`
      return o
    },
    {}
  )

  return obj
}, {})

export const buildRoutes = (formatMessage, options = {}) =>
  Object.entries(config).map(([key, route]) => {
    const { withBreadcrumbs = false } = options

    const routeProps = {
      path: formatMessage(messages[key], pathValues[key]),
      element: <route.component />,
      name: key,
    }

    if (withBreadcrumbs && route.breadcrumb) {
      routeProps.breadcrumb = route.breadcrumb(formatMessage)
    }

    return routeProps
  })

export default config
